import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { environment } from '../../environments/environment';
import { LicensingAuthService } from '../../services/licensing-auth.service';

/** The possible states during the auth callback exchange. */
type AuthCallbackState = 'loading' | 'exchanging' | 'authenticated' | 'error';

/** CSS files served by the backend for the shared auth UI. */
const AUTH_STYLE_FILES: Array<string> = ['auth-shared.css', 'auth-callback.css'];

/** Delay before redirecting to the app root after a successful sign-in, giving the success state time to render. */
const REDIRECT_DELAY_MS: number = 1500;

/**
 * Auth Callback Component.
 * Handles the return redirect from the backend authentication page.
 *
 * The backend's own `auth-callback.html` is what Okta redirects to (registered
 * `redirect_uri`); in cross-origin mode that page relays the auth code + PKCE
 * verifier back to this SPA via `{returnUrl}?auth=pending&relay_token=...`.
 * This component handles that arrival — it can't be replaced by the backend
 * page without rearchitecting same-origin sessions — but it pulls every visual
 * asset (CSS, favicon, background image) directly from the backend, so the
 * brief flash matches the backend's login and callback pages exactly.
 *
 * On initialization it:
 * 1. Injects the backend-served auth CSS (auth-shared.css, auth-callback.css) and
 *    sets the background image — producing the same polished card UI as the shell-app.
 * 2. Reads the `relay_token` and `auth=pending` parameters from the URL.
 * 3. Delegates the full code exchange and session establishment to LicensingAuthService.
 * 4. Navigates to the app root on success, or shows an error with retry on failure.
 *
 * On destroy the injected styles and body overrides are removed.
 *
 * TODO(de-dup): Functionally identical to
 * `apps/shell-app/src/app/components/auth-callback/auth-callback.component.ts` +
 * `apps/shell-app/src/app/services/auth-callback.service.ts`. The state machine,
 * accordion error UI, and CSS-injection logic could all be lifted into
 * `@deighton/shared-ui/auth` once both apps share a common auth-service interface.
 */
@Component({
  selector: 'd-licensing-auth-callback',
  imports: [TranslocoModule],
  templateUrl: './auth-callback.component.html',
  styleUrl: './auth-callback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  private readonly authService: LicensingAuthService = inject(LicensingAuthService);
  private readonly router: Router = inject(Router);

  /** Injected document reference — avoids direct global access for testability/SSR safety. */
  private readonly document: Document = inject(DOCUMENT);

  /** Current state of the callback exchange process. */
  public readonly authState: WritableSignal<AuthCallbackState> = signal<AuthCallbackState>('loading');

  /** Technical error message to show when state is 'error'. */
  public readonly error: WritableSignal<string | null> = signal<string | null>(null);

  /** URL of the Deighton favicon served by the backend assets endpoint. */
  public readonly faviconUrl: string = `${environment.platformApiBaseUrl}/assets/favicon.svg`;

  /** Handle for the post-success redirect timer, cleared on destroy to avoid navigating after teardown. */
  private redirectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** Injects backend styles, then processes the auth callback. */
  public ngOnInit(): void {
    this.injectSharedStyles();
    void this.handleCallbackExchange();
  }

  /** Cancels any pending redirect, then removes injected styles and body overrides on destroy. */
  public ngOnDestroy(): void {
    if (this.redirectTimeoutId !== null) {
      clearTimeout(this.redirectTimeoutId);
      this.redirectTimeoutId = null;
    }
    this.removeSharedStyles();
  }

  /** Restarts the login flow by redirecting to the backend auth page. */
  public onRetry(): void {
    this.authService.redirectToLogin();
  }

  /**
   * Processes the callback: validates `auth=pending`, delegates to LicensingAuthService,
   * then navigates to the root on success or sets the error state on failure.
   */
  private async handleCallbackExchange(): Promise<void> {
    const params: URLSearchParams = new URLSearchParams(window.location.search);
    const authStatus: string | null = params.get('auth');

    if (authStatus !== 'pending') {
      this.error.set('Missing "auth=pending" parameter. The login flow may not have completed correctly.');
      this.authState.set('error');
      return;
    }

    this.authState.set('exchanging');

    try {
      await this.authService.handleAuthCallback();
      this.authState.set('authenticated');
      this.redirectTimeoutId = setTimeout((): void => {
        this.redirectTimeoutId = null;
        void this.router.navigate(['/']);
      }, REDIRECT_DELAY_MS);
    } catch (err: unknown) {
      const message: string = err instanceof Error ? err.message : String(err);
      console.error('[AuthCallback] Callback exchange failed:', message);
      this.error.set(message);
      this.authState.set('error');
    }
  }

  /**
   * Injects the backend-served auth CSS files into `<head>` and applies
   * the background image to `<body>` — matching the shell-app's auth page styling.
   */
  private injectSharedStyles(): void {
    AUTH_STYLE_FILES.forEach((file: string): void => {
      const elementId: string = `auth-style-${file.replace('.', '-')}`;
      if (this.document.getElementById(elementId)) {
        return;
      }
      const link: HTMLLinkElement = this.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `${environment.platformApiBaseUrl}/auth/${file}`;
      link.id = elementId;
      link.onerror = (): void => {
        console.warn(`[AuthCallback] Could not load shared auth style: ${file}`);
      };
      this.document.head.appendChild(link);
    });

    const body: HTMLElement = this.document.body;
    body.classList.add('auth-callback-active');
    body.style.backgroundImage = `url(${environment.platformApiBaseUrl}/assets/auth-bg.jpg)`;
    body.style.backgroundRepeat = 'no-repeat';
    body.style.backgroundPosition = 'center center';
    body.style.backgroundSize = 'cover';
    body.style.minHeight = '100vh';
  }

  /**
   * Removes injected auth CSS link elements and clears body style overrides.
   */
  private removeSharedStyles(): void {
    AUTH_STYLE_FILES.forEach((file: string): void => {
      const elementId: string = `auth-style-${file.replace('.', '-')}`;
      this.document.getElementById(elementId)?.remove();
    });

    const body: HTMLElement = this.document.body;
    body.classList.remove('auth-callback-active');
    body.style.backgroundImage = '';
    body.style.backgroundRepeat = '';
    body.style.backgroundPosition = '';
    body.style.backgroundSize = '';
    body.style.minHeight = '';
  }
}
